import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@/components/icon/Icon";
import { CountUp } from "@/components/motion/CountUp";
import { Reveal } from "@/components/motion/Reveal";
import { QuoteProcessDialog } from "@/components/sections/QuoteProcessDialog";
import { DELIVERABLES, PAYMENT_STEPS } from "@/data/process";
import { FALLBACK_PRICING_CONFIG } from "@/data/pricing";
import { api } from "@/lib/api";
import { gsap, useGSAP } from "@/lib/gsap";
import { usePrefersReducedMotion } from "@/lib/reduced-motion";
import { track } from "@/lib/track";
import { useInViewport } from "@/lib/use-in-viewport";
import {
  applyOnlineDiscount,
  calculateQuote,
  formatWon,
  formatWonShort,
  normalizeConfig,
  optionsForType,
  presetSelection,
  presetsForType,
  reconcileSelection,
  toQuoteSnapshot,
  type QuoteSelection,
} from "@/lib/quote";
import type { PricingConfigDTO, PricingItem, PricingPreset } from "@/types/pricing";

/** 티어 라벨에는 금액을 넣지 않아요 — 단가만 고쳐도 문구가 어긋나지 않게 여기서 붙여요. */
const tierLabel = (tier: PricingItem) =>
  tier.amount > 0 ? `${tier.label} · 월 ${formatWonShort(tier.amount)}` : tier.label;

/** 원 단위 금액 롤업. */
const AnimatedWon = ({ value, className = "wds-title-2 text-label-strong" }: { value: number; className?: string }) => (
  <CountUp value={value} format={formatWon} className={className} />
);

/** ? info affordance with a token-styled tooltip shown on hover + keyboard/tap focus. */
function InfoTooltip({ text }: { text: string }) {
  return (
    <span className="group/tip relative inline-flex align-middle">
      <button
        type="button"
        aria-label="설명 보기"
        // Sits inside a selectable <label>; stop the click from toggling the option.
        onClick={(e) => e.stopPropagation()}
        className="inline-flex items-center rounded-full opacity-60 transition-opacity duration-200 ease-standard hover:opacity-100 focus-visible:opacity-100"
      >
        <Icon name="circleInfo" size={15} />
      </button>
      <span
        role="tooltip"
        className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 w-56 -translate-x-1/2 rounded-md bg-static-black px-3 py-2 wds-caption-1 font-normal leading-relaxed text-static-white opacity-0 shadow-normal transition-opacity duration-200 ease-standard group-hover/tip:opacity-100 group-focus-within/tip:opacity-100"
      >
        {text}
      </span>
    </span>
  );
}

export function Quote() {
  const navigate = useNavigate();
  // 단가·옵션의 원본은 DB(어드민). 폴백 스냅샷으로 먼저 그리고 응답이 오면 교체해요.
  const [config, setConfig] = useState<PricingConfigDTO>(FALLBACK_PRICING_CONFIG);
  const body = config.body;
  const [sel, setSel] = useState<QuoteSelection>({
    type: FALLBACK_PRICING_CONFIG.body.project_types[0].key,
    pages: 1,
    options: [],
    support: FALLBACK_PRICING_CONFIG.body.support_tiers[0].key,
  });
  const [notes, setNotes] = useState("");
  // 페이지 수는 편집 중 빈 값·선행 0을 허용하려고 문자열로 들고, 숫자로 파싱해 sel에 반영해요.
  const [pagesInput, setPagesInput] = useState(String(sel.pages));
  // 마우스를 올리거나(데스크탑) 포커스한(키보드) 항목의 설명을 그룹 하단에 보여줘요.
  const [hint, setHint] = useState<{ group: string; text: string } | null>(null);
  const [processOpen, setProcessOpen] = useState(false);
  // 적용한 프리셋 키. 사용자가 항목을 직접 만지면 배지만 사라지고 선택값은 남아요 —
  // 프리셋에서 한두 개만 빼고 싶은 흐름이 가장 흔해요.
  const [applied, setApplied] = useState<string | null>(null);
  const hintFor = (group: string, fallback: string) => (hint?.group === group ? hint.text : fallback);

  useEffect(() => {
    let alive = true;
    api
      .get<PricingConfigDTO>("/pricing-config")
      .then((next) => {
        if (!alive) return;
        const normalized = normalizeConfig(next);
        setConfig(normalized);
        // 단가가 갱신되며 사라진 항목이 선택돼 있을 수 있어요.
        setSel((prev) => reconcileSelection(prev, normalized.body));
      })
      // 실패하면 폴백 단가로 계속 — 견적·문의 흐름은 살려둬요.
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  // 고른 유형에 붙는 옵션만 보여줘요 — 랜딩에 대시보드, 모바일앱에 "앱 동반 개발" 같은 조합을 막아요.
  const usableOptions = useMemo(() => optionsForType(body.options, sel.type), [body.options, sel.type]);
  const features = useMemo(() => usableOptions.filter((o) => o.group === "feature"), [usableOptions]);
  const addons = useMemo(() => usableOptions.filter((o) => o.group === "addon"), [usableOptions]);
  const discountPct = Math.round(body.online_discount_rate * 100);

  // presets 는 페치 경계(normalizeConfig)에서 이미 채워져요.
  // 펼치면 모든 유형의 예시를 유형별로 묶어 한 번에 보여줘요 — 고른 유형만 걸러내면 기본 유형(랜딩)에서
  // 칩이 하나만 떠서 "예시" 라는 게 뭔지 전달이 안 돼요. 프리셋을 고르면 프로젝트 유형까지 함께 채워져요.
  const presetGroups = useMemo(
    () =>
      body.project_types
        .map((type) => ({ type, presets: presetsForType(body.presets, type.key) }))
        .filter((group) => group.presets.length > 0),
    [body.presets, body.project_types],
  );
  const appliedPreset = body.presets.find((p) => p.key === applied) ?? null;

  const applyPreset = (preset: PricingPreset) => {
    const next = presetSelection(preset, body);
    setSel(next);
    setPagesInput(String(next.pages));
    setApplied(preset.key);
    track("quote_preset", { detail: preset.key });
  };

  /**
   * 프리셋 배지를 지우고 페이지·옵션·월 지원을 초기값으로 되돌려요.
   * 유형은 그대로 둬요 — 프리셋이 채운 유형이라도 지금 화면에 보이는 값이라, 되돌리면 방금 본 것과
   * 달라져서 더 놀라요. 유형은 바로 위에서 언제든 직접 바꿀 수 있어요.
   */
  const clearPreset = () => {
    const next = reconcileSelection(
      { type: sel.type, pages: 1, options: [], support: body.support_tiers[0]?.key ?? sel.support },
      body,
    );
    setSel(next);
    setPagesInput(String(next.pages));
    setApplied(null);
  };

  const result = useMemo(() => calculateQuote(sel, body), [sel, body]);
  // 표준 구축가 → 온라인 할인가 + 상한(clamp). 월 정액은 상한과 별개.
  const price = useMemo(
    () => applyOnlineDiscount(result.oneTime, body.online_discount_rate, body.onetime_cap),
    [result.oneTime, body.online_discount_rate, body.onetime_cap],
  );
  // 월 0원 티어("없음")에 "첫 3개월 무료"는 뜻이 없어요. 0 이면 아래 표시가 지금과 같은 한 줄로 떨어져요.
  const freeMonths = result.monthly > 0 ? body.monthly_free_months : 0;
  const monthlySaved = result.monthly * freeMonths;

  const handleInquiry = () => {
    const snapshot = toQuoteSnapshot(sel, config, price, result, freeMonths);
    track("quote_computed", {
      quote: {
        config_version: snapshot.config_version,
        type: snapshot.type,
        pages: snapshot.pages,
        option_keys: snapshot.options.map((o) => o.key),
        support: snapshot.support,
        standard: snapshot.standard,
        discounted: snapshot.discounted,
        monthly: snapshot.monthly,
      },
    });
    navigate("/inquiry", { state: { quote: snapshot, notes: notes.trim() } });
  };

  const toggleOption = (key: string) => {
    track("quote_option", { detail: key });
    setApplied(null);
    setSel((prev) => ({
      ...prev,
      options: prev.options.includes(key) ? prev.options.filter((k) => k !== key) : [...prev.options, key],
    }));
  };

  // 모바일에선 견적 패널이 긴 폼 아래로 밀려서, 항목을 고르는 동안 금액이 안 보여요.
  // 견적 섹션 안에 있고 패널이 아직 화면에 없을 때만 하단에 금액 바를 띄워요.
  const reduceMotion = usePrefersReducedMotion();
  const sectionRef = useRef<HTMLElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const inSection = useInViewport(sectionRef);
  const panelVisible = useInViewport(panelRef, 0.3);
  const showBar = inSection && !panelVisible;

  // 바는 언마운트하지 않고 아래로 밀어 감춰요 — 다시 나타날 때마다 금액을 0부터 세지 않게요.
  // `y: 0` 이 꼭 있어야 해요: 첫 상태를 Tailwind `translate-y-full` 로 잡아뒀는데, GSAP 은
  // 계산된 matrix 에서 그 100% 를 px(=바 높이)로 읽어 yPercent 를 0 으로 봐요. y 를 같이 0 으로
  // 몰지 않으면 `yPercent: 0` 이 아무것도 안 바꾸고 바가 화면 밖에 그대로 남아요.
  useGSAP(
    () => {
      gsap.to(barRef.current, {
        y: 0,
        yPercent: showBar ? 0 : 110,
        duration: reduceMotion ? 0 : 0.28,
        ease: "power2.out",
        overwrite: true,
      });
    },
    { dependencies: [showBar, reduceMotion] },
  );

  return (
    <section ref={sectionRef} id="quote" className="scroll-mt-20 px-6 py-24">
      <div className="mx-auto flex max-w-layout flex-col gap-10">
        <Reveal className="flex flex-col gap-3">
          <span aria-hidden="true" className="h-1 w-10 rounded-full bg-primary" />
          <h2 className="wds-title-1 text-label-strong">직접 만들어본 팀이 견적을 냅니다</h2>
          <p className="wds-body-1-reading text-label-neutral">
            외주 개발도 자사 제품을 만들 때와 같은 기준으로 진행해요. 프로젝트 범위를 알려주시면
            빠르고 정확한 견적을 안내해 드려요.
          </p>
        </Reveal>

        <div className="grid gap-10 lg:grid-cols-[1.2fr_1fr]">
          <Reveal delay={0.05} className="flex flex-col gap-8">
            <fieldset className="flex flex-col gap-3">
              <legend className="wds-headline-2 mb-1 flex items-center gap-1.5 text-label-strong">
                프로젝트 유형
                <InfoTooltip text="만들 결과물의 형태예요. 유형에 따라 기본 구성과 가격이 달라져요." />
              </legend>
              <div className="flex flex-wrap gap-3">
                {body.project_types.map((t) => (
                  <label
                    key={t.key}
                    onMouseEnter={() => setHint({ group: "type", text: t.desc })}
                    onMouseLeave={() => setHint(null)}
                    className={`wds-label-1 flex cursor-pointer items-center gap-2 rounded-md px-4 py-2 transition-colors duration-200 ease-standard ${
                      sel.type === t.key
                        ? "bg-primary text-static-white"
                        : "bg-background-alternative text-label-neutral"
                    }`}
                  >
                    <input
                      type="radio"
                      name="projectType"
                      value={t.key}
                      checked={sel.type === t.key}
                      onChange={() => {
                        setApplied(null);
                        setSel((prev) => reconcileSelection({ ...prev, type: t.key }, body));
                      }}
                      onFocus={() => setHint({ group: "type", text: t.desc })}
                      onBlur={() => setHint(null)}
                      className="sr-only"
                    />
                    {t.label}
                  </label>
                ))}
              </div>
              <p className="min-h-[2.5rem] wds-caption-1 leading-relaxed text-label-neutral">
                {hintFor("type", body.project_types.find((t) => t.key === sel.type)?.desc ?? "")}
              </p>
            </fieldset>

            {/*
              프리셋 진입. 필수 선택(유형) 아래에 접힌 한 줄로만 둬요 — 칩을 누르면 견적이 곧바로
              수백만 원대로 뛰어서, 아직 무엇을 만들지 정하지 않은 방문자에게 첫인상이 금액이 되면 안 돼요.
              적용 배지는 <summary> 에 둬서 접어도 무엇을 담았는지 보여요.
              -mt-4: 부모 flex(gap-8)가 준 간격 절반을 상쇄해 이 줄이 유형 블록에 붙어 보이게 해요 —
              지우면 유형과 프리셋 사이가 다른 블록 간격과 같아져 소속이 안 읽혀요.
            */}
            {presetGroups.length > 0 && (
              <div className="-mt-4 flex items-start gap-3">
                <details className="flex-1">
                  <summary className="flex cursor-pointer list-none items-center gap-1.5 wds-caption-1 text-label-neutral">
                    <Icon
                      name={appliedPreset ? "circleCheckFill" : "chevronRight"}
                      size={14}
                      className={
                        appliedPreset
                          ? "shrink-0 text-primary"
                          : "shrink-0 transition-transform duration-200 ease-standard [details[open]_&]:rotate-90"
                      }
                    />
                    {appliedPreset ? (
                      <span>'{appliedPreset.label}' 구성을 담았어요</span>
                    ) : (
                      "어떤 기능이 필요한지 모르겠다면 예시로 채워보세요"
                    )}
                  </summary>

                  {/*
                    칩은 라디오가 아니라 버튼이에요. 라디오 그룹이면 키보드가 화살표로만 이동하고 이동
                    자체가 선택이라, 훑어보려던 사용자가 지나가는 프리셋마다 폼을 덮어쓰고 track 을
                    발사해요(프리셋 사용률 지표 오염). 버튼은 Tab 으로 지나가고 Enter/Space 로만 적용돼요.
                    aria-pressed 로 적용 상태를 알리고, 접근명에 유형을 붙여요(보이는 글자 p.label 을
                    접근명이 포함해 WCAG 2.5.3 을 지켜요).
                  */}
                  <div className="mt-3 flex flex-col gap-4">
                    {presetGroups.map(({ type, presets }) => (
                      <fieldset key={type.key} className="flex flex-col gap-2">
                        <legend className="wds-caption-1 mb-1 font-semibold text-label-assistive">
                          {type.label}
                        </legend>
                        <div className="flex flex-wrap gap-3">
                          {presets.map((p) => (
                            <button
                              key={p.key}
                              type="button"
                              aria-pressed={applied === p.key}
                              aria-label={`${type.label} · ${p.label}`}
                              onClick={() => applyPreset(p)}
                              onMouseEnter={() => setHint({ group: "preset", text: `${p.label} — ${p.desc}` })}
                              onMouseLeave={() => setHint(null)}
                              onFocus={() => setHint({ group: "preset", text: `${p.label} — ${p.desc}` })}
                              onBlur={() => setHint(null)}
                              className={`wds-label-1 flex cursor-pointer items-center gap-2 rounded-md px-4 py-2 transition-colors duration-200 ease-standard ${
                                applied === p.key
                                  ? "bg-primary text-static-white"
                                  : "bg-background-alternative text-label-neutral"
                              }`}
                            >
                              {applied === p.key && <Icon name="check" size={14} />}
                              {p.label}
                            </button>
                          ))}
                        </div>
                      </fieldset>
                    ))}
                    <p className="min-h-[2.5rem] wds-caption-1 leading-relaxed text-label-neutral">
                      {hintFor("preset", "가까운 예시를 고르면 프로젝트 유형까지 함께 채워져요.")}
                    </p>
                  </div>
                </details>

                {appliedPreset && (
                  <button
                    type="button"
                    onClick={clearPreset}
                    aria-label={`'${appliedPreset.label}' 구성 해제`}
                    className="-my-1 wds-caption-1 shrink-0 px-1 py-1 font-semibold text-primary underline underline-offset-4"
                  >
                    해제
                  </button>
                )}
              </div>
            )}

            <div className="flex flex-col gap-3">
              <span className="flex items-center gap-1.5">
                <label htmlFor="quote-pages" className="wds-headline-2 text-label-strong">
                  페이지 수
                </label>
                <InfoTooltip text="제작할 화면(페이지)의 대략적인 개수예요. 기본 구성에 포함된 수를 넘는 만큼 금액에 더해져요." />
              </span>
              <input
                id="quote-pages"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={pagesInput}
                onChange={(e) => {
                  // 숫자만 남기고 선행 0을 제거해요("031" → "31"). 빈 값은 편집 중 허용(견적은 0으로 계산).
                  const digits = e.target.value.replace(/\D/g, "").replace(/^0+(?=\d)/, "");
                  // 상한을 넘겨 입력하면 입력칸 자체를 상한으로 되돌려요 — 999페이지 같은 값이 견적을 망가뜨려요.
                  const parsed = digits === "" ? 0 : Math.min(Number.parseInt(digits, 10), body.max_pages);
                  setApplied(null);
                  setPagesInput(digits === "" ? "" : String(parsed));
                  setSel((prev) => ({ ...prev, pages: parsed }));
                }}
                onBlur={() => setPagesInput(String(sel.pages))}
                className="wds-body-1 w-32 rounded-md border border-line-neutral bg-background-normal px-4 py-2 text-label-normal tabular-nums"
              />
            </div>

            {features.length > 0 && (
              <fieldset className="flex flex-col gap-3">
                <legend className="wds-headline-2 mb-1 flex items-center gap-1.5 text-label-strong">
                  필요한 기능
                  <InfoTooltip text="프로젝트에 넣을 기능이에요. 고른 만큼 견적에 더해져요. 항목에 마우스를 올리면 설명이 보여요." />
                </legend>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {features.map((f) => {
                    const checked = sel.options.includes(f.key);
                    return (
                      <label
                        key={f.key}
                        onMouseEnter={() => setHint({ group: "feature", text: `${f.label} — ${f.desc}` })}
                        onMouseLeave={() => setHint(null)}
                        className={`wds-label-1 flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 transition-colors duration-200 ease-standard ${
                          checked ? "bg-primary text-static-white" : "bg-background-alternative text-label-neutral"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleOption(f.key)}
                          onFocus={() => setHint({ group: "feature", text: `${f.label} — ${f.desc}` })}
                          onBlur={() => setHint(null)}
                          className="sr-only"
                        />
                        {checked && <Icon name="check" size={14} />}
                        {f.label}
                      </label>
                    );
                  })}
                </div>
                <p className="min-h-[2.5rem] wds-caption-1 leading-relaxed text-label-neutral">
                  {hintFor("feature", "항목에 마우스를 올리거나 선택하면 설명이 보여요.")}
                </p>
              </fieldset>
            )}

            {addons.length > 0 && (
              <div className="flex flex-col gap-3">
                <span className="wds-headline-2 flex items-center gap-1.5 text-label-strong">
                  추가 옵션
                  <InfoTooltip text="구축과 별개로 함께 진행할 수 있는 옵션이에요." />
                </span>
                {addons.map((a) => (
                  <label
                    key={a.key}
                    className="flex cursor-pointer items-center justify-between gap-4 rounded-md bg-background-alternative px-4 py-3"
                  >
                    <span className="flex flex-col">
                      <span className="wds-body-2 text-label-normal">{a.label}</span>
                      <span className="wds-caption-1 text-label-assistive">{a.desc}</span>
                    </span>
                    <input
                      type="checkbox"
                      checked={sel.options.includes(a.key)}
                      onChange={() => toggleOption(a.key)}
                      className="h-5 w-5 accent-primary"
                    />
                  </label>
                ))}
              </div>
            )}

            <fieldset className="flex flex-col gap-3">
              <legend className="wds-headline-2 mb-1 flex items-center gap-1.5 text-label-strong">
                월 지원
                <InfoTooltip text="출시 후 지원 방식이에요. 하나만 선택하며, 운영위탁은 유지보수를 포함해요." />
              </legend>
              {body.support_tiers.map((o) => (
                <label
                  key={o.key}
                  onMouseEnter={() => setHint({ group: "support", text: o.desc })}
                  onMouseLeave={() => setHint(null)}
                  className={`flex cursor-pointer items-center justify-between gap-4 rounded-md px-4 py-3 transition-colors duration-200 ease-standard ${
                    sel.support === o.key
                      ? "bg-primary text-static-white"
                      : "bg-background-alternative text-label-normal"
                  }`}
                >
                  <span className="wds-body-2">{tierLabel(o)}</span>
                  <input
                    type="radio"
                    name="support"
                    value={o.key}
                    checked={sel.support === o.key}
                    onChange={() => {
                      setApplied(null);
                      setSel((prev) => ({ ...prev, support: o.key }));
                    }}
                    onFocus={() => setHint({ group: "support", text: o.desc })}
                    onBlur={() => setHint(null)}
                    className="sr-only"
                  />
                  {sel.support === o.key && <Icon name="check" size={16} />}
                </label>
              ))}
              <p className="min-h-[2.5rem] wds-caption-1 leading-relaxed text-label-neutral">
                {hintFor("support", body.support_tiers.find((o) => o.key === sel.support)?.desc ?? "")}
              </p>
            </fieldset>

            <div className="flex flex-col gap-3">
              <span className="flex items-center gap-1.5">
                <label htmlFor="quote-notes" className="wds-headline-2 text-label-strong">추가 요청사항</label>
                <InfoTooltip text="위 항목에 없는 기능이나 참고할 내용을 자유롭게 적어주세요. 문의하기를 누르면 함께 전달돼요." />
              </span>
              <textarea
                id="quote-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="원하시는 기능이나 참고 사항을 자유롭게 적어주세요."
                className="wds-body-2 rounded-md border border-line-neutral bg-background-normal px-4 py-3 text-label-normal placeholder:text-label-assistive"
              />
              {notes.trim() && (
                <p className="wds-caption-1 text-status-cautionary">
                  직접 적어주신 내용은 예상 견적에 자동 반영되지 않아, 실제 견적과 달라질 수 있어요.
                </p>
              )}
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div
              ref={panelRef}
              className="sticky top-24 flex flex-col gap-6 rounded-lg bg-background-elevated p-8 shadow-normal"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="wds-label-1 text-label-assistive">예상 견적</span>
                {/*
                  배지 조건은 선택과 무관한 body 값이에요 — 월 지원을 "없음" 으로 둔 방문자에게도
                  "고르면 첫 N개월 무료" 라는 정보는 유효해요.
                  두 번째 배지는 테두리형이에요. 같은 색 알약 두 개가 붙으면 하나로 뭉쳐 보여요.
                */}
                <span className="flex flex-wrap justify-end gap-2">
                  <span className="rounded-full bg-primary px-3 py-1 wds-caption-1 font-semibold text-static-white">
                    온라인 견적 {discountPct}% 할인
                  </span>
                  {body.monthly_free_months > 0 && (
                    <span className="rounded-full border border-primary px-3 py-1 wds-caption-1 font-semibold text-primary tabular-nums">
                      월 유지보수 첫 {body.monthly_free_months}개월 무료
                    </span>
                  )}
                </span>
              </div>
              <output className="flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                  <span className="wds-caption-1 text-label-assistive">초기 구축 비용</span>
                  <div className="flex items-baseline gap-2">
                    <span className="wds-body-2 text-label-assistive line-through">{formatWon(price.standard)}</span>
                    <span className="wds-caption-1 font-semibold text-primary">{discountPct}% ↓</span>
                  </div>
                  <AnimatedWon value={price.discounted} className="wds-title-2 font-bold text-primary" />
                  <span className="wds-caption-1 font-medium text-primary">{formatWon(price.savings)} 아꼈어요</span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="flex items-center gap-1.5 wds-caption-1 text-label-assistive">
                    월 유지보수 비용
                    <InfoTooltip text="위에서 고른 월 지원의 금액이에요. 운영위탁은 유지보수를 포함해요." />
                  </span>
                  {freeMonths > 0 ? (
                    <>
                      <div className="flex items-baseline gap-2">
                        <span className="wds-body-2 text-label-assistive line-through tabular-nums">
                          {formatWon(result.monthly)}
                        </span>
                        <span className="wds-caption-1 font-semibold text-primary tabular-nums">
                          첫 {freeMonths}개월 무료
                        </span>
                      </div>
                      <AnimatedWon value={0} className="wds-title-2 font-bold text-primary" />
                      <span className="wds-caption-1 font-medium text-primary tabular-nums">
                        {formatWon(monthlySaved)} 아꼈어요
                      </span>
                      {/* 0원만 크게 뜨면 월 비용이 계속 공짜로 읽혀요. */}
                      <span className="wds-caption-1 text-label-assistive tabular-nums">
                        {freeMonths + 1}개월차부터 월 {formatWon(result.monthly)}
                      </span>
                    </>
                  ) : (
                    <AnimatedWon value={result.monthly} />
                  )}
                </div>
              </output>
              <p className="wds-caption-1 text-label-assistive">
                실제 견적과 다를 수 있는 예상 금액이에요. 지금 온라인으로 문의 주시면 표준가에서 {discountPct}% 할인된
                금액으로 시작할 수 있어요.
                {body.monthly_free_months > 0 && ` 월 유지보수는 첫 ${body.monthly_free_months}개월 무료예요.`}
              </p>
              <div className="flex flex-col gap-2 border-line-alternative border-t pt-5">
                <span className="wds-label-1 font-semibold text-label-strong">받으시는 것</span>
                <ul className="flex flex-col gap-2">
                  {DELIVERABLES.map((item) => (
                    <li key={item} className="flex gap-2">
                      <Icon name="circleCheckFill" size={16} className="mt-0.5 shrink-0 text-primary" />
                      <span className="wds-caption-1 leading-relaxed text-label-neutral">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              {/* 완성품을 받아본 뒤에 잔금을 낸다 — 문의 직전 부담을 덜어주는 자리라 버튼 바로 위에 둬요. */}
              <div className="flex flex-col gap-3 rounded-md border border-primary bg-background-normal p-4">
                <span className="wds-label-1 font-semibold text-label-strong">
                  잔금은 마음에 드실 때 받아요
                </span>
                <ol className="flex flex-col gap-3">
                  {PAYMENT_STEPS.map((step, index) => (
                    <li key={step.title} className="flex gap-3">
                      <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-primary wds-caption-1 font-bold text-static-white tabular-nums">
                        {index + 1}
                      </span>
                      <span className="flex flex-col gap-0.5">
                        <span className="wds-body-2 font-semibold text-label-strong">{step.title}</span>
                        <span className="wds-caption-1 leading-relaxed text-label-neutral">{step.body}</span>
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
              <button
                type="button"
                onClick={handleInquiry}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-primary px-6 wds-body-2 font-semibold text-static-white transition-colors duration-200 ease-standard hover:bg-primary-strong"
              >
                문의하기
                <Icon name="arrowRight" size={16} />
              </button>
              <button
                type="button"
                onClick={() => {
                  track("quote_process_opened");
                  setProcessOpen(true);
                }}
                aria-haspopup="dialog"
                className="wds-label-1 inline-flex items-center justify-center gap-1 font-semibold text-primary underline underline-offset-4"
              >
                진행 절차·계약 안내 보기
                <Icon name="chevronRight" size={14} />
              </button>
              <p className="flex items-center justify-center gap-1.5 wds-caption-1 text-label-assistive">
                <Icon name="circleCheckFill" size={14} className="text-primary" />
                문의 주시면 24시간 이내에 답변드려요.
              </p>
            </div>
          </Reveal>
        </div>
      </div>

      {/*
        모바일 전용 금액 바. 슬라이드로 감췄다 보이기만 해서(언마운트 없음) 다시 나타날 때마다
        금액을 0부터 세지 않아요. 숨겨진 동안엔 inert 로 키보드·스크린리더에서도 빠져요.
      */}
      <div
        ref={barRef}
        inert={!showBar}
        aria-hidden={!showBar}
        className="fixed inset-x-0 bottom-0 z-40 translate-y-full border-line-alternative border-t bg-background-elevated px-6 pt-3 shadow-normal lg:hidden"
        // 홈 인디케이터가 있는 기기에서 버튼이 가려지지 않게 해요.
        style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
      >
        <div className="mx-auto flex max-w-layout items-center justify-between gap-4">
          <span className="flex flex-col">
            <span className="wds-caption-1 text-label-assistive">예상 견적 · {discountPct}% 할인가</span>
            <AnimatedWon value={price.discounted} className="wds-headline-1 font-bold text-primary" />
          </span>
          <button
            type="button"
            onClick={handleInquiry}
            className="inline-flex h-11 shrink-0 items-center justify-center gap-2 rounded-md bg-primary px-5 wds-body-2 font-semibold text-static-white transition-colors duration-200 ease-standard hover:bg-primary-strong"
          >
            문의하기
            <Icon name="arrowRight" size={16} />
          </button>
        </div>
      </div>

      <QuoteProcessDialog open={processOpen} onClose={() => setProcessOpen(false)} />
    </section>
  );
}

export default Quote;
